import React from 'react';
import { WALKTHROUGH_COLUMN_KEYS, WALKTHROUGH_DEFAULT_COLUMNS, WALKTHROUGH_COLUMN_LABELS, WALKTHROUGH_LABEL_KEYS, walkthroughLabels, walkthroughInlineUid } from './walkthroughColumns';
import ColorPicker from './ColorPicker';
import { NEXT_STEPS_DEFAULTS as NS_DEF, NEXT_STEPS_PANELS, nextStepsDefaultConfig } from './nextStepsDefaults';

/**
 * The findings walkthrough's settings.
 *
 * Every colour and size the renderers read is here, because the band ships
 * with house styling and the point of a widget is that somebody else can make
 * it theirs without editing a renderer. The defaults shown in each control are
 * the same literals the DOCX and PDF renderers fall back to -- if one of them
 * changes, this panel starts lying about what the report will look like.
 */
const WALKTHROUGH_SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Info'];
const WALKTHROUGH_SEVERITY_BG = {
  Critical: '#7f1d1d', High: '#dc2626', Medium: '#ea580c', Low: '#ca8a04', Info: '#2e86c1',
};

function FindingsWalkthroughConfig({ config, update }) {
  const inp = {
    padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)', boxSizing: 'border-box',
    border: '1px solid var(--border)', background: 'var(--bg-input)',
    color: 'var(--text)', fontFamily: 'inherit', minWidth: 0,
  };
  const lbl = { fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
  const row = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
  const group = {
    display: 'flex', flexDirection: 'column', gap: 7, padding: 8,
    borderRadius: 'var(--radius)', border: '1px solid var(--border)',
  };
  const btn = { background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 11, padding: '1px 6px',
                lineHeight: 1.4 };
  const cap = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.5px', color: 'var(--text-secondary)' };

  const num = (key, fallback, min, max, width = 52) => (
    <input type="number" min={min} max={max} value={config[key] ?? fallback}
      onChange={e => update(key, Number(e.target.value) || fallback)}
      style={{ ...inp, width }} />
  );
  // allowNone={false}: every colour here is required -- a header row has a
  // background whether or not anyone chose one -- so the picker's "None" chip
  // would have set the value to null and been coerced straight back to the
  // fallback, leaving a swatch that reads as selected and changes nothing.
  const colour = (key, fallback, title) => (
    <ColorPicker label={title} size={20} allowNone={false} value={config[key] || fallback}
      onChange={c => update(key, c || fallback)} />
  );

  const columns = Array.isArray(config.columns) && config.columns.length
    ? config.columns : [...WALKTHROUGH_DEFAULT_COLUMNS];
  const toggleColumn = (key) => {
    const picked = columns.includes(key) ? columns.filter(c => c !== key) : [...columns, key];
    // Sorted into the order the renderers print in, so a column ticked later
    // lands in its proper place instead of on the end -- ticking Exploitable
    // puts it between CVSS and Status, where its heading sits in this panel.
    const next = WALKTHROUGH_COLUMN_KEYS.filter(k => picked.includes(k));
    // Never leave the table with no columns -- it would render a border and
    // nothing else, which reads as a broken report rather than a setting.
    update('columns', next.length ? next : [key]);
  };

  // The headings the renderers will actually print -- which is where the
  // finding column's heading becomes 'Title' once the identifier is split
  // out. The tick boxes keep the house names, so the list of columns stays
  // stable even when an author blanks a heading.
  const labels = walkthroughLabels(config);
  const inlineUid = walkthroughInlineUid(config);

  const sevFilter = Array.isArray(config.severityFilter) ? config.severityFilter : [];
  const toggleSeverity = (s) =>
    update('severityFilter', sevFilter.includes(s) ? sevFilter.filter(x => x !== s) : [...sevFilter, s]);

  // ── Next steps ───────────────────────────────────────────────────
  // The block is optional and its absence is meaningful: see
  // nextStepsConfig in backend/utils/report-helpers.js.
  const ns = config.nextSteps && typeof config.nextSteps === 'object' ? config.nextSteps : null;
  const nsOn = !!ns && ns.enabled !== false;
  // `[]` means the author removed every panel, and all three renderers draw
  // no block for it. Falling back to the house panels here showed three
  // populated panels in the editor beside a preview that printed none.
  // Only a MISSING panels key takes the defaults, which is what the
  // renderers do too.
  const nsPanels = nsOn
    ? (Array.isArray(ns.panels) ? ns.panels : NEXT_STEPS_PANELS)
        .map(p => ({ title: String(p?.title ?? ''), body: String(p?.body ?? '') }))
    : [];
  const updateNs = (key, value) => update('nextSteps', { ...(ns || {}), [key]: value });
  const setPanels = (panels) => updateNs('panels', panels);
  const setPanel = (i, key, value) =>
    setPanels(nsPanels.map((p, j) => (j === i ? { ...p, [key]: value } : p)));
  const addPanel = () => setPanels([...nsPanels, { title: '', body: '' }]);
  const removePanel = (i) => setPanels(nsPanels.filter((_, j) => j !== i));
  const movePanel = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= nsPanels.length) return;
    const next = [...nsPanels];
    [next[i], next[j]] = [next[j], next[i]];
    setPanels(next);
  };
  const nsColour = (key, title) => (
    <ColorPicker label={title} size={20} allowNone={false} value={(ns && ns[key]) || NS_DEF[key]}
      onChange={c => updateNs(key, c || NS_DEF[key])} />
  );
  const nsNum = (key, min, max) => (
    <input type="number" min={min} max={max} value={(ns && ns[key]) ?? NS_DEF[key]}
      onChange={e => {
        const n = Number(e.target.value);
        // >= 0, not > 0: the gap control's minimum IS zero, and treating
        // that as "unset" meant typing 0 silently put the default back.
        updateNs(key, Number.isFinite(n) && n >= 0 ? Math.min(max, n) : NS_DEF[key]);
      }}
      onBlur={e => {
        const n = Number(e.target.value);
        updateNs(key, Number.isFinite(n) && n >= 0 ? Math.min(max, Math.max(min, n)) : NS_DEF[key]);
      }}
      style={{ ...inp, width: 52 }} />
  );

  const sevColours = { ...WALKTHROUGH_SEVERITY_BG, ...(config.severityColors || {}) };
  const setSevColour = (s, c) =>
    update('severityColors', { ...(config.severityColors || {}), [s]: c || WALKTHROUGH_SEVERITY_BG[s] });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <input value={config.heading || ''} placeholder="Heading &mdash; blank for none"
        onChange={e => update('heading', e.target.value)} style={{ ...inp, width: '100%' }} />

      <div style={group}>
        <span style={cap}>Columns</span>
        <div style={row}>
          {WALKTHROUGH_COLUMN_KEYS.map(k => (
            <label key={k} style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={columns.includes(k)} onChange={() => toggleColumn(k)}
                style={{ margin: 0, cursor: 'pointer' }} />
              {WALKTHROUGH_COLUMN_LABELS[k]}
            </label>
          ))}
        </div>
        <div style={row}>
          <span style={lbl}>Headings:</span>
          {/* One box per column the table is showing, in the order it shows
              them -- a heading for a column that is not on screen is a control
              with nothing to change. */}
          {columns.map(k => (
            <input key={k} value={labels[k]}
              placeholder={WALKTHROUGH_COLUMN_LABELS[k]}
              aria-label={`${WALKTHROUGH_COLUMN_LABELS[k]} column heading`}
              onChange={e => update(WALKTHROUGH_LABEL_KEYS[k], e.target.value)}
              style={{ ...inp, width: 86 }} />
          ))}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Header row</span>
        <div style={row}>
          <span style={lbl}>Background:</span>{colour('headerBg', '#1f3864', 'Header background')}
          <span style={lbl}>Text:</span>{colour('headerColor', '#ffffff', 'Header text')}
          <span style={lbl}>Size:</span>{num('headerSize', 9, 5, 20)}
          <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.headerBold !== false}
              onChange={e => update('headerBold', e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
            Bold
          </label>
          <span style={lbl}>Borders:</span>{colour('borderColor', '#d8dfe8', 'Border colour')}
        </div>
      </div>

      {columns.includes('uid') && (
        <div style={group}>
          <span style={cap}>Finding ID column</span>
          <div style={row}>
            <span style={lbl}>Colour:</span>{colour('uidColor', '#1f3864', 'Finding ID colour')}
            <span style={lbl}>Size:</span>{num('uidSize', 9, 5, 24)}
            <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={config.uidBold === true}
                onChange={e => update('uidBold', e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
              Bold
            </label>
            <span style={lbl}>Width:</span>{num('uidWidth', 1700, 400, 6000, 66)}
          </div>
        </div>
      )}

      <div style={group}>
        <span style={cap}>Finding column</span>
        <div style={row}>
          <span style={lbl}>Title:</span>{colour('titleColor', '#1f3864', 'Title colour')}
          <span style={lbl}>Size:</span>{num('titleSize', 9, 5, 24)}
          {/* The inline identifier and the Finding ID column are the same
              information in two places, so only one of them is offered at a
              time. Unticking the column brings these back as they were. */}
          {inlineUid || !columns.includes('uid') ? (
            <>
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={config.showUid !== false}
                  onChange={e => update('showUid', e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
                Show finding ID
              </label>
              <span style={lbl}>Separator:</span>
              <input value={config.uidSeparator ?? '  |  '} onChange={e => update('uidSeparator', e.target.value)}
                style={{ ...inp, width: 60 }} />
            </>
          ) : (
            <span style={{ ...lbl, fontStyle: 'italic' }}>
              The finding ID has a column of its own, so this cell holds the title alone.
            </span>
          )}
        </div>
        <div style={row}>
          <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.showDescription !== false}
              onChange={e => update('showDescription', e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
            Description line
          </label>
          <select value={config.descriptionField || 'details'}
            onChange={e => update('descriptionField', e.target.value)} style={{ ...inp, width: 130 }}>
            {/* Only fields a finding actually has. Impact was offered here
                once and is not a column on `findings` -- it lives on
                vulnerability_templates -- so choosing it produced a
                description line that was always blank and never complained.
                Kept in step with WALKTHROUGH_DESCRIPTION_FIELDS in
                backend/utils/report-helpers.js. */}
            <option value="details">Details</option>
            <option value="observation">Observation</option>
            <option value="recommendation">Recommendation</option>
            {/* Not a field: the finding cell prints its headline and nothing
                under it. The same result as clearing the checkbox beside this,
                offered here because this is where an author looks when
                deciding what the second line should say. */}
            <option value="none">Heading only</option>
          </select>
          <span style={lbl}>Colour:</span>{colour('descriptionColor', '#5f6b7a', 'Description colour')}
          <span style={lbl}>Size:</span>{num('descriptionSize', 8, 5, 20)}
          {/* A walkthrough row is a summary; the whole finding is in the
              detailed section. Without a cap, a long details field makes a
              table row taller than a page, which the PDF table draws by
              moving the whole row to a fresh page and then overrunning it. */}
          <span style={lbl}>Max chars:</span>
          <input type="number" min={0} max={2000} value={config.descriptionMaxChars ?? 280}
            title="0 means no limit"
            onChange={e => update('descriptionMaxChars', Math.max(0, Number(e.target.value) || 0))}
            style={{ ...inp, width: 66 }} />
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Severity</span>
        <div style={row}>
          <span style={lbl}>Style:</span>
          <select value={config.severityStyle || 'block'} onChange={e => update('severityStyle', e.target.value)}
            style={{ ...inp, width: 128 }}>
            <option value="block">Filled block</option>
            <option value="text">Coloured text</option>
          </select>
          {config.severityStyle !== 'text' && (
            <>
              <span style={lbl}>Text:</span>{colour('severityTextColor', '#ffffff', 'Severity text')}
            </>
          )}
          <span style={lbl}>Size:</span>{num('severitySize', 8, 5, 20)}
          <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.severityUpper !== false}
              onChange={e => update('severityUpper', e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
            Caps
          </label>
        </div>
        <div style={row}>
          {WALKTHROUGH_SEVERITIES.map(s => (
            <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={lbl}>{s}:</span>
              <ColorPicker label={`${s} colour`} size={18} allowNone={false} value={sevColours[s]}
                onChange={c => setSevColour(s, c)} />
            </span>
          ))}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>CVSS and status</span>
        <div style={row}>
          <span style={lbl}>CVSS:</span>{colour('cvssColor', '#1f3864', 'CVSS colour')}
          {num('cvssSize', 9, 5, 20)}
          <span style={lbl}>Status:</span>{colour('statusColor', '#c00000', 'Status colour')}
          {num('statusSize', 9, 5, 20)}
          <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.statusBold !== false}
              onChange={e => update('statusBold', e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
            Bold
          </label>
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Which findings</span>
        <div style={row}>
          {WALKTHROUGH_SEVERITIES.map(s => (
            <label key={s} style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={sevFilter.includes(s)} onChange={() => toggleSeverity(s)}
                style={{ margin: 0, cursor: 'pointer' }} />
              {s}
            </label>
          ))}
          <span style={lbl}>Max rows:</span>
          <input type="number" min={0} max={500} value={config.maxRows ?? 0}
            title="0 shows every finding"
            onChange={e => update('maxRows', Math.max(0, Number(e.target.value) || 0))}
            style={{ ...inp, width: 60 }} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Nothing ticked means every severity, and 0 rows means every finding. Ticking
          none would otherwise print an empty table, which reads as &ldquo;no findings&rdquo;
          rather than as a filter.
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Next steps</span>
        <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          {/* Two different ways of being off, and the difference matters.
              A widget that never had a block stays ABSENT, which is what
              keeps a template saved before this existed from printing Next
              steps twice. A block the author switches off keeps its panels
              and records enabled:false, so turning it back on does not throw
              away the text they wrote. Both read as off in all three
              renderers. */}
          <input type="checkbox" checked={nsOn}
            onChange={e => update('nextSteps', e.target.checked
              ? (ns ? { ...ns, enabled: true } : nextStepsDefaultConfig())
              : (ns ? { ...ns, enabled: false } : null))}
            style={{ margin: 0, cursor: 'pointer' }} />
          Show a Next steps block beneath the table
        </label>

        {nsOn && (
          <>
            <div style={row}>
              <span style={lbl}>Caption:</span>
              <input value={ns.caption ?? NS_DEF.caption} placeholder="Blank for none"
                onChange={e => updateNs('caption', e.target.value)} style={{ ...inp, width: 150 }} />
              {nsColour('captionColor', 'Caption colour')}
              {nsNum('captionSize', 5, 30)}
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={ns.captionBold !== false}
                  onChange={e => updateNs('captionBold', e.target.checked)}
                  style={{ margin: 0, cursor: 'pointer' }} />
                Bold
              </label>
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={ns.captionCaps === true}
                  onChange={e => updateNs('captionCaps', e.target.checked)}
                  style={{ margin: 0, cursor: 'pointer' }} />
                Caps
              </label>
            </div>

            {nsPanels.map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                  {/* Bounded, because the PDF table cannot split a row: a body
                      long enough to make the row taller than the page is drawn
                      past the bottom of it. Kept in step with
                      NEXT_STEPS_TITLE_MAX / NEXT_STEPS_BODY_MAX in
                      backend/utils/report-helpers.js. */}
                  <input value={p.title} maxLength={120} placeholder={`Panel ${i + 1} title`}
                    onChange={e => setPanel(i, 'title', e.target.value)}
                    style={{ ...inp, width: '100%', fontWeight: 600 }} />
                  <textarea value={p.body} rows={3} maxLength={800} placeholder="What the reader should do"
                    onChange={e => setPanel(i, 'body', e.target.value)}
                    style={{ ...inp, width: '100%', resize: 'vertical', lineHeight: 1.4 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <button type="button" title="Move up" onClick={() => movePanel(i, -1)} disabled={i === 0}
                    style={{ ...btn, opacity: i === 0 ? 0.4 : 1 }}>&uarr;</button>
                  <button type="button" title="Move down" onClick={() => movePanel(i, 1)}
                    disabled={i === nsPanels.length - 1}
                    style={{ ...btn, opacity: i === nsPanels.length - 1 ? 0.4 : 1 }}>&darr;</button>
                  {/* Never down to zero: an empty panel list renders nothing at
                      all, which would leave this switch saying the block is on
                      while the report shows no block. Turning it off is what
                      the checkbox above is for. */}
                  <button type="button" title="Remove panel" onClick={() => removePanel(i)}
                    disabled={nsPanels.length <= 1}
                    style={{ ...btn, color: '#ef4444', opacity: nsPanels.length <= 1 ? 0.4 : 1 }}>&times;</button>
                </div>
              </div>
            ))}

            <div style={row}>
              <button type="button" onClick={addPanel} disabled={nsPanels.length >= 6}
                style={{ ...btn, padding: '3px 10px', opacity: nsPanels.length >= 6 ? 0.4 : 1 }}>
                + Panel
              </button>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Panels share the row evenly. Six is the ceiling &mdash; past that each
                one is too narrow to read.
              </span>
            </div>

            <div style={row}>
              <span style={lbl}>Panel fill:</span>{nsColour('panelBg', 'Panel background')}
              <span style={lbl}>Title:</span>{nsColour('titleColor', 'Panel title colour')}
              {nsNum('titleSize', 5, 24)}
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={ns.titleBold !== false}
                  onChange={e => updateNs('titleBold', e.target.checked)}
                  style={{ margin: 0, cursor: 'pointer' }} />
                Bold
              </label>
            </div>
            <div style={row}>
              <span style={lbl}>Body:</span>{nsColour('bodyColor', 'Panel body colour')}
              {nsNum('bodySize', 5, 24)}
              <span style={lbl}>Gap:</span>{nsNum('panelGap', 0, 10)}
              <span style={lbl}>Borders:</span>{nsColour('borderColor', 'Panel border colour')}
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={ns.showBorder !== false}
                  onChange={e => updateNs('showBorder', e.target.checked)}
                  style={{ margin: 0, cursor: 'pointer' }} />
                Show
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FindingsWalkthroughConfig;
